import { Server } from "lucide-react";
import ChannelHeader from "../components/channel/ChannelHeader";
import { useEffect, useState } from "react";
import apiClient from "../utils/apiClient";
import { useParams, Link } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { imageBaseurl } from "../utils/common";
import LivePlayer from "../components/match/LivePlayer2";
import NoStreamingCard from "../components/match/NoStreamingCard";

export default function ChannelPage() {

  const [events, setEvents] =
    useState({});

  const [loadingEvents, setLoadingEvents] =
    useState(false);

  const [selectedServer, setSelectedServer] =
    useState(null);

  const { slug } = useParams();

  // ============================
  // GET CHANNEL
  // ============================
  const getEvents = async () => {

    try {

      setLoadingEvents(true);

      const response =
        await apiClient.get(
          "/public/channel/" + slug
        );

      const result =
        response?.data;

      if (result?.success) {

        const data =
          result.data;

        // PARSE SERVERS
        const parsedServers =
          typeof data.servers === "string"
            ? JSON.parse(JSON.parse(
              data.servers
            ))
            : data.servers || [];

        data.servers =
          parsedServers;

        setEvents(data);

        // DEFAULT SERVER
        if (
          parsedServers?.length > 0
        ) {

          const defaultServer =
            parsedServers.find(
              (item) =>
                item.is_default
            );

          setSelectedServer(
            defaultServer ||
            parsedServers[0]
          );
        }
      }

    } catch (error) {

      console.log(
        "Channel API Error:",
        error
      );

    } finally {

      setLoadingEvents(false);
    }
  };

  useEffect(() => {
    getEvents();
  }, [slug]);

  // ============================
  // LOADING
  // ============================
  if (loadingEvents) {
    return (
      <div className="flex justify-center py-10">
        <CircularProgress />
      </div>
    );
  }

  // ============================
  // NOT FOUND
  // ============================
  if (!events?.id) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">

        <div className="text-center">

          <h2 className="text-2xl font-bold text-red-500 mb-3">
            Channel Not Found
          </h2>

          <p className="text-gray-500 mb-5">
            Try another channel.
          </p>

          <Link
            to="/"
            className="
                        inline-flex
                        px-5 py-2.5
                        rounded-lg
                        bg-gradient-to-r
                        from-blue-600
                        to-pink-500
                        text-white
                        font-semibold
                        "
          >
            Go To Channels
          </Link>

        </div>

      </main>
    );
  }

  return (
    <main className="flex-1 py-6 bg-white dark:bg-gray-950 min-h-screen">

      <div className="container mx-auto px-4 max-w-6xl">

        {/* HEADER */}

        <ChannelHeader
          logo={imageBaseurl + events?.logo}
          title={
            events?.channel_name
          }
          subtitle={
            events?.short_title
          }
        />

        {/* VIDEO PLAYER */}

        <div className="mb-6 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow">

          <div className="relative aspect-video bg-black">

            {selectedServer?.stream_url ? (
              <>
                {selectedServer?.stream_type === 'iframe' ? (
                  <iframe
                    src={
                      selectedServer.stream_url
                    }
                    className="absolute inset-0 w-full h-full"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  />
                ) : (
                  <LivePlayer streamUrl={selectedServer.stream_url} />
                )}
              </>
            ) : (
              <NoStreamingCard />
            )}
          </div>

        </div>

        {/* SERVERS */}

        <div className="mb-6">

          <div className="flex items-center gap-2 mb-3">

            <Server
              size={16}
              className="text-gray-500"
            />

            <span className="text-sm font-medium text-gray-500">
              Select Server:
            </span>

          </div>

          <div className="flex flex-wrap gap-3">

            {events?.servers?.map(
              (
                server,
                index
              ) => (

                <button
                  key={index}
                  onClick={() =>
                    setSelectedServer(
                      server
                    )
                  }
                  className={`
                                    px-4 py-2
                                    rounded-lg
                                    min-w-[120px]
                                    font-semibold
                                    text-white
                                    transition-all
                                    hover:scale-105

                                    ${selectedServer?.stream_url ===
                      server.stream_url
                      ? "bg-gradient-to-r from-blue-600 to-pink-500"
                      : "bg-gray-700"
                    }
                                    `}
                >

                  {
                    server.server_name
                  }

                </button>
              )
            )}

          </div>

        </div>

        {/* ABOUT */}

        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow p-6">

          <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-blue-500 to-pink-500 bg-clip-text text-transparent">

            About {
              events?.channel_name
            }

          </h2>

          <div className="space-y-4 text-gray-600 dark:text-gray-400">

            <p>
              {
                events?.description
              }
            </p>

          </div>

        </div>

      </div>

    </main>
  );
}